/**
 * OI Momentum — detects multi-strike OI unwinding/buildup, suggests trades,
 * and provides a full backtest using futures OI data.
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";

const API = "/api/zerodha";

const DIR_COLORS = {
  BULLISH: { bg: "var(--tint-pos-strong)", color: "#34d399", border: "#059669" },
  BEARISH: { bg: "var(--tint-neg)", color: "#f87171", border: "#dc2626" },
  NEUTRAL: { bg: "var(--tint-indigo)", color: "var(--pale-indigo)", border: "#6366f1" },
};

const CLASS_COLORS = {
  LONG_BUILDUP: "#34d399",
  SHORT_COVERING: "var(--pale-pos)",
  SHORT_BUILDUP: "#f87171",
  LONG_UNWINDING: "var(--pale-neg)",
  NEUTRAL: "#94a3b8",
};

function Badge({ text, color }) {
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: color + "22", color, border: `1px solid ${color}` }}>{text}</span>;
}

export default function OIMomentum() {
  const [index, setIndex] = useState("NIFTY");
  const [expiry, setExpiry] = useState("");
  const [expiries, setExpiries] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanInterval, setScanInterval] = useState("30");
  const scanRef = useRef(null);

  // Backtest state
  const [activeView, setActiveView] = useState("scan");
  const [btData, setBtData] = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [btError, setBtError] = useState("");
  const [btDays, setBtDays] = useState("30");
  const [btSL, setBtSL] = useState("30");
  const [btTarget, setBtTarget] = useState("50");
  const [btStreak, setBtStreak] = useState("2");
  const [btThreshold, setBtThreshold] = useState("3");
  const [btHold, setBtHold] = useState("20");
  const [btLots, setBtLots] = useState("5");
  const [btTimeframe, setBtTimeframe] = useState("15minute");

  const fetchScan = useCallback(async (idx, exp) => {
    setLoading(true); setError("");
    try {
      const params = { index: idx };
      if (exp) params.expiry = exp;
      const res = await axios.get(`${API}/oi-momentum`, { params, timeout: 30000 });
      if (res.data.success) {
        setData(res.data.data);
        if (res.data.data.expiries?.length) {
          setExpiries(res.data.data.expiries);
          if (!exp) setExpiry(res.data.data.expiry);
        }
      } else setError(res.data.message);
    } catch (err) { setError(err.response?.data?.message || err.message); }
    setLoading(false);
  }, []);

  const toggleScan = useCallback(() => {
    if (scanning) {
      if (scanRef.current) { clearInterval(scanRef.current); scanRef.current = null; }
      setScanning(false);
    } else {
      setScanning(true);
      fetchScan(index, expiry);
      scanRef.current = setInterval(() => fetchScan(index, expiry), parseInt(scanInterval) * 1000);
    }
  }, [scanning, index, expiry, scanInterval, fetchScan]);

  useEffect(() => { return () => { if (scanRef.current) clearInterval(scanRef.current); }; }, []);

  const handleIndexChange = (idx) => {
    setIndex(idx); setExpiry(""); setExpiries([]);
    if (scanning) { clearInterval(scanRef.current); scanRef.current = null; setScanning(false); }
    fetchScan(idx, "");
  };

  const handleExpiryChange = (exp) => { setExpiry(exp); fetchScan(index, exp); };

  const runBacktest = async () => {
    setBtLoading(true); setBtError(""); setBtData(null);
    try {
      const res = await axios.get(`${API}/oi-momentum/backtest`, {
        params: { index, days: btDays, sl: btSL, target: btTarget, streak: btStreak, threshold: btThreshold, hold: btHold, lots: btLots, timeframe: btTimeframe },
        timeout: 60000,
      });
      if (res.data.success) setBtData(res.data.data);
      else setBtError(res.data.message);
    } catch (err) { setBtError(err.response?.data?.message || err.message); }
    setBtLoading(false);
  };

  const m = data?.momentum;
  const sig = data?.signal;
  const dc = m ? DIR_COLORS[m.netDirection] || DIR_COLORS.NEUTRAL : null;

  return (
    <div className="five-ema">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h2>OI Momentum</h2>
          <p className="dashboard-subtitle">Multi-strike OI unwinding detection + IV-adjusted trade signals</p>
        </div>
        <div className="dashboard-actions">
          {["NIFTY", "BANKNIFTY", "SENSEX"].map(idx => (
            <button key={idx} className={`btn btn-sm ${index === idx ? "btn-primary" : ""}`}
              onClick={() => handleIndexChange(idx)} disabled={loading}>{idx}</button>
          ))}
          {expiries.length > 0 && (
            <select className="ema-select" value={expiry} onChange={e => handleExpiryChange(e.target.value)} disabled={loading}>
              {expiries.map(exp => <option key={exp} value={exp}>{exp}{exp === expiries[0] ? " (Current)" : ""}</option>)}
            </select>
          )}
          <select className="ema-select" value={scanInterval} onChange={e => setScanInterval(e.target.value)} disabled={scanning}>
            <option value="15">15s</option><option value="30">30s</option><option value="60">1m</option><option value="300">5m</option>
          </select>
          <button className={`btn ${scanning ? "btn-danger" : "btn-primary"}`} onClick={toggleScan} disabled={loading && !scanning}>
            {loading && !scanning ? "Loading..." : scanning ? "Stop Scan" : "Start Scan"}
          </button>
        </div>
      </div>

      {/* View toggle */}
      <div style={{ display: "flex", gap: 8, padding: "8px 12px", borderBottom: "1px solid var(--surface3)" }}>
        <button className={`btn btn-sm ${activeView === "scan" ? "btn-primary" : ""}`} onClick={() => setActiveView("scan")}>Live Scan</button>
        <button className={`btn btn-sm ${activeView === "backtest" ? "btn-primary" : ""}`} onClick={() => setActiveView("backtest")}>Backtest</button>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {/* ===== SCAN VIEW ===== */}
      {activeView === "scan" && <>

        {/* Info bar */}
        {data && (
          <div style={{ display: "flex", gap: 16, padding: "8px 12px", background: "var(--panel3)", borderBottom: "1px solid var(--surface3)", fontSize: 12, color: "var(--grey-text2)", flexWrap: "wrap", alignItems: "center" }}>
            <span>Spot: <strong style={{ color: "#fff" }}>{data.spot?.toFixed(1)}</strong></span>
            <span>ATM: <strong style={{ color: "#fff" }}>{data.atmStrike}</strong></span>
            <span>Expiry: <strong>{data.expiry}</strong></span>
            <span>DTE: <strong style={{ color: data.daysToExpiry <= 2 ? "#f87171" : "#34d399" }}>{data.daysToExpiry}</strong></span>
            <span>PCR: <strong style={{ color: data.pcr > 1.2 ? "#34d399" : data.pcr < 0.8 ? "#f87171" : "#fbbf24" }}>{data.pcr}</strong></span>
            <span>HV: <strong style={{ color: "#8b5cf6" }}>{data.hv20}%</strong></span>
            <span style={{ color: "var(--edge)" }}>|</span>
            <span>ATM CE IV: <strong style={{ color: "#60a5fa" }}>{data.atmIV?.ceIV || "-"}%</strong></span>
            <span>ATM PE IV: <strong style={{ color: "#fb923c" }}>{data.atmIV?.peIV || "-"}%</strong></span>
            {data.isFirstScan && <span style={{ color: "#f59e0b" }}>(Baseline captured — OI changes will show on next scan)</span>}
            <span style={{ color: "var(--btn-border)", fontSize: 10 }}>{data.asOf}</span>
          </div>
        )}

        {/* Momentum banner */}
        {m && m.signals.length > 0 && (
          <div style={{ padding: "10px 16px", margin: "8px 12px", borderRadius: 6, background: dc.bg, border: `1px solid ${dc.border}`, color: dc.color }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
              {m.netDirection} MOMENTUM (Bull: {m.bullishScore} | Bear: {m.bearishScore})
            </div>
            {m.signals.map((s, i) => (
              <div key={i} style={{ fontSize: 12, marginTop: 4, opacity: 0.9 }}>
                <Badge text={s.strength} color={s.direction === "BULLISH" ? "#34d399" : "#f87171"} /> {s.detail}
              </div>
            ))}
          </div>
        )}
        {m && m.signals.length === 0 && data && !data.isFirstScan && (
          <div style={{ padding: "10px 16px", margin: "8px 12px", borderRadius: 6, background: "var(--tint-indigo)", border: "1px solid #6366f1", color: "var(--pale-indigo)", fontSize: 13 }}>
            No multi-strike unwinding detected yet. Waiting for OI patterns to develop...
          </div>
        )}

        {/* Trade signal card */}
        {sig && sig.action !== "NO_TRADE" && (
          <div style={{ margin: "8px 12px", padding: 16, borderRadius: 8, background: sig.direction === "BULLISH" ? "var(--tint-pos-strong)" : "var(--tint-neg)", border: `1px solid ${sig.direction === "BULLISH" ? "#059669" : "#dc2626"}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: sig.direction === "BULLISH" ? "#34d399" : "#f87171" }}>
                  {sig.action} @ {sig.strike}
                </div>
                <div style={{ fontSize: 12, color: "var(--grey-text)", marginTop: 4 }}>
                  <Badge text={sig.confidence} color={sig.confidence === "HIGH" ? "#34d399" : "#fbbf24"} />
                  {" "}Premium: <strong style={{ color: "#fff" }}>{sig.premium}</strong>
                  {" "}| SL: <strong style={{ color: "#f87171" }}>{sig.slPremium} (-{sig.slPct}%)</strong>
                  {" "}| Target: <strong style={{ color: "#34d399" }}>{sig.targetPremium} (+{sig.targetPct}%)</strong>
                  {" "}| DTE: {sig.dte}
                </div>
              </div>
              <div>
                <Badge text={`IV: ${sig.iv || "-"}%`} color={sig.ivTag === "CHEAP" ? "#34d399" : sig.ivTag === "EXPENSIVE" ? "#f87171" : "#fbbf24"} />
                {" "}
                <Badge text={sig.ivTag || "N/A"} color={sig.ivTag === "CHEAP" ? "#34d399" : sig.ivTag === "EXPENSIVE" ? "#f87171" : "#fbbf24"} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: "var(--grey-text)", marginTop: 8 }}>{sig.rationale}</div>
          </div>
        )}

        {/* OI Table */}
        {data?.strikes?.length > 0 && (
          <div style={{ padding: "8px 12px" }}>
            <h4 style={{ margin: "0 0 8px", fontSize: 13, color: "var(--grey-text)" }}>OI Chain (ATM ± 10 strikes)</h4>
            <div className="stocks-table-wrapper">
              <table className="stocks-table" style={{ fontSize: 11 }}>
                <thead>
                  <tr>
                    <th colSpan={5} style={{ textAlign: "center", background: "#1a2744", color: "#60a5fa", borderRight: "2px solid var(--surface3)" }}>CALL (CE)</th>
                    <th style={{ background: "var(--panel3)", textAlign: "center" }}>Strike</th>
                    <th colSpan={5} style={{ textAlign: "center", background: "#2d1f1f", color: "#fb923c" }}>PUT (PE)</th>
                  </tr>
                  <tr>
                    <th style={{ color: "#60a5fa" }}>OI Chg%</th>
                    <th style={{ color: "#60a5fa" }}>OI Change</th>
                    <th style={{ color: "#60a5fa" }}>OI</th>
                    <th style={{ color: "#60a5fa" }}>Volume</th>
                    <th style={{ color: "#60a5fa", borderRight: "2px solid var(--surface3)" }}>LTP</th>
                    <th></th>
                    <th style={{ color: "#fb923c" }}>LTP</th>
                    <th style={{ color: "#fb923c" }}>Volume</th>
                    <th style={{ color: "#fb923c" }}>OI</th>
                    <th style={{ color: "#fb923c" }}>OI Change</th>
                    <th style={{ color: "#fb923c" }}>OI Chg%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.strikes.map((s, i) => {
                    const isATM = s.isATM;
                    const ceChg = s.ce?.oiChange || 0;
                    const peChg = s.pe?.oiChange || 0;
                    const ceChgPct = s.ce?.oiChangePct || 0;
                    const peChgPct = s.pe?.oiChangePct || 0;
                    return (
                      <tr key={i} style={{ background: isATM ? "rgba(139,92,246,0.1)" : undefined }}>
                        <td style={{ color: ceChgPct > 2 ? "#34d399" : ceChgPct < -2 ? "#f87171" : "var(--grey-text2)", fontWeight: Math.abs(ceChgPct) > 5 ? 700 : 400 }}>
                          {ceChgPct > 0 ? "+" : ""}{ceChgPct}%
                        </td>
                        <td style={{ color: ceChg > 0 ? "#34d399" : ceChg < 0 ? "#f87171" : "var(--edge)", fontSize: 10 }}>
                          {ceChg > 0 ? "+" : ""}{(ceChg / 1000).toFixed(0)}K
                        </td>
                        <td style={{ color: "#94a3b8" }}>{((s.ce?.oi || 0) / 100000).toFixed(1)}L</td>
                        <td style={{ color: "var(--edge)", fontSize: 10 }}>{((s.ce?.volume || 0) / 1000).toFixed(0)}K</td>
                        <td style={{ fontWeight: 600, color: "#e2e8f0", borderRight: "2px solid var(--surface3)" }}>{s.ce?.ltp?.toFixed(1) || "-"}</td>
                        <td style={{ textAlign: "center", fontWeight: 700, fontSize: 12, color: isATM ? "#8b5cf6" : "#e2e8f0", background: isATM ? "rgba(139,92,246,0.2)" : undefined }}>
                          {s.strike}{isATM && <div style={{ fontSize: 8, color: "#8b5cf6" }}>ATM</div>}
                        </td>
                        <td style={{ fontWeight: 600, color: "#e2e8f0" }}>{s.pe?.ltp?.toFixed(1) || "-"}</td>
                        <td style={{ color: "var(--edge)", fontSize: 10 }}>{((s.pe?.volume || 0) / 1000).toFixed(0)}K</td>
                        <td style={{ color: "#94a3b8" }}>{((s.pe?.oi || 0) / 100000).toFixed(1)}L</td>
                        <td style={{ color: peChg > 0 ? "#34d399" : peChg < 0 ? "#f87171" : "var(--edge)", fontSize: 10 }}>
                          {peChg > 0 ? "+" : ""}{(peChg / 1000).toFixed(0)}K
                        </td>
                        <td style={{ color: peChgPct > 2 ? "#34d399" : peChgPct < -2 ? "#f87171" : "var(--grey-text2)", fontWeight: Math.abs(peChgPct) > 5 ? 700 : 400 }}>
                          {peChgPct > 0 ? "+" : ""}{peChgPct}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 10, color: "var(--edge)", marginTop: 4 }}>
              Green OI change = buildup (new positions). Red OI change = unwinding (positions closing). OI in Lakhs, change in Thousands.
            </div>
          </div>
        )}
      </>}

      {/* ===== BACKTEST VIEW ===== */}
      {activeView === "backtest" && (
        <div className="bt-section">
          <div className="bt-header">
            <h3>OI Momentum Backtest (Intraday, Volume-Momentum)</h3>
            <div className="bt-controls">
              <select className="ema-select" value={btTimeframe} onChange={e => setBtTimeframe(e.target.value)}>
                <option value="5minute">5 Min</option><option value="15minute">15 Min</option><option value="30minute">30 Min</option>
              </select>
              <select className="ema-select" value={btDays} onChange={e => setBtDays(e.target.value)}>
                <option value="5">5 Days</option><option value="10">10 Days</option><option value="20">20 Days</option>
                <option value="30">30 Days</option><option value="60">60 Days</option>
              </select>
              <select className="ema-select" value={btSL} onChange={e => setBtSL(e.target.value)}>
                <option value="20">SL 20%</option><option value="30">SL 30%</option><option value="40">SL 40%</option><option value="50">SL 50%</option>
              </select>
              <select className="ema-select" value={btTarget} onChange={e => setBtTarget(e.target.value)}>
                <option value="30">Tgt 30%</option><option value="50">Tgt 50%</option><option value="75">Tgt 75%</option><option value="100">Tgt 100%</option>
              </select>
              <select className="ema-select" value={btStreak} onChange={e => setBtStreak(e.target.value)}>
                <option value="2">Streak 2+</option><option value="3">Streak 3+</option><option value="4">Streak 4+</option>
              </select>
              <select className="ema-select" value={btHold} onChange={e => setBtHold(e.target.value)}>
                <option value="10">Max 10 bars</option><option value="20">Max 20 bars</option><option value="30">Max 30 bars</option><option value="50">Max 50 bars</option>
              </select>
              <select className="ema-select" value={btLots} onChange={e => setBtLots(e.target.value)}>
                <option value="1">1 Lot</option><option value="2">2 Lots</option><option value="5">5 Lots</option><option value="10">10 Lots</option>
              </select>
              <button className="btn btn-secondary" onClick={runBacktest} disabled={btLoading}>{btLoading ? "Running..." : "Run Backtest"}</button>
            </div>
          </div>

          {btError && <div className="error-msg">{btError}</div>}
          {btLoading && <div className="loading"><div className="spinner" /><p>Running OI momentum backtest...</p></div>}

          {btData && !btLoading && (() => {
            const s = btData.summary;
            return (
              <div className="backtest-results">
                <h3>Backtest — {s.index} ({s.timeframe}, {s.totalDays || "?"} days, {s.totalBars} bars)</h3>
                <p className="bt-candle-count">SL: {s.slPct}% | Target: {s.targetPct}% | Min streak: {s.minStreak} candles | Max hold: {s.maxHoldCandles} candles | Forced close at 15:20 IST</p>

                <div className={`bt-money-banner ${s.totalPnl >= 0 ? "money-profit" : "money-loss"}`}>
                  <div className="bt-money-amount">{s.totalPnl >= 0 ? "+" : ""}₹{Math.abs(s.totalPnl).toLocaleString("en-IN")}</div>
                  <div className="bt-money-detail">{s.lots} lots × {s.lotSize} qty = {s.qty} units | {s.totalTrades} trades | ROI: {s.roi}%</div>
                </div>

                <div className="bt-summary-grid">
                  <div className={`bt-card ${s.totalPnl >= 0 ? "bt-card-green" : "bt-card-red"}`}><div className="bt-card-value">{s.totalPnl >= 0 ? "+" : ""}₹{Math.abs(s.totalPnl).toLocaleString("en-IN")}</div><div className="bt-card-label">Net P&L</div></div>
                  <div className="bt-card"><div className="bt-card-value">{s.totalTrades}</div><div className="bt-card-label">Total Trades</div></div>
                  <div className={`bt-card ${s.winRate >= 50 ? "bt-card-green" : "bt-card-red"}`}><div className="bt-card-value">{s.winRate}%</div><div className="bt-card-label">Win Rate</div></div>
                  <div className={`bt-card ${s.profitFactor >= 1 ? "bt-card-green" : "bt-card-red"}`}><div className="bt-card-value">{s.profitFactor === Infinity ? "INF" : s.profitFactor}</div><div className="bt-card-label">Profit Factor</div></div>
                  <div className="bt-card bt-card-red"><div className="bt-card-value">₹{s.maxDrawdown?.toLocaleString("en-IN")}</div><div className="bt-card-label">Max Drawdown</div></div>
                  <div className="bt-card"><div className="bt-card-value">{s.avgHoldCandles} bars</div><div className="bt-card-label">Avg Hold</div></div>
                </div>

                <div className="bt-dir-pnl-cards">
                  <div className={`bt-dir-card bt-dir-card-long ${s.bullTrades > 0 && s.bullWinRate >= 50 ? "bt-card-green" : "bt-card-red"}`}>
                    <div className="bt-dir-card-title">BULLISH (Buy CE)</div>
                    <div className="bt-dir-card-meta">{s.bullTrades} trades | {s.bullWinRate}% win</div>
                  </div>
                  <div className={`bt-dir-card bt-dir-card-short ${s.bearTrades > 0 && s.bearWinRate >= 50 ? "bt-card-green" : "bt-card-red"}`}>
                    <div className="bt-dir-card-title">BEARISH (Buy PE)</div>
                    <div className="bt-dir-card-meta">{s.bearTrades} trades | {s.bearWinRate}% win</div>
                  </div>
                </div>

                {/* Equity curve */}
                {btData.equity?.length > 0 && (
                  <div className="bt-equity">
                    <h4>Equity Curve ({btData.equity.length} trades)</h4>
                    <div className="bt-equity-chart">
                      {(() => {
                        const eq = btData.equity;
                        const vals = [...eq.map(p => p.pnl), 0];
                        const maxV = Math.max(...vals), minV = Math.min(...vals);
                        const range = maxV - minV || 1, h = 140, w = eq.length * 20 + 20;
                        const yOf = v => h - ((v - minV) / range) * h;
                        return (
                          <svg width="100%" height={h + 30} viewBox={`0 0 ${w} ${h + 30}`}>
                            <line x1="0" y1={yOf(0)} x2={w} y2={yOf(0)} stroke="var(--border-strong)" strokeDasharray="4" />
                            <polyline fill="none" stroke={eq[eq.length - 1]?.pnl >= 0 ? "#69f0ae" : "#ff5252"} strokeWidth="2"
                              points={eq.map((p, i) => `${i * 20 + 10},${yOf(p.pnl)}`).join(" ")} />
                            {eq.map((p, i) => (
                              <circle key={i} cx={i * 20 + 10} cy={yOf(p.pnl)} r="3" fill={p.type === "BULLISH" ? "#69f0ae" : "#ff5252"}>
                                <title>{`${p.type}: ${p.pnl >= 0 ? "+" : ""}₹${p.pnl.toLocaleString("en-IN")}`}</title>
                              </circle>
                            ))}
                          </svg>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Trade log */}
                <div className="bt-trade-log">
                  <h4>Trade Log ({btData.trades?.length} trades)</h4>
                  <div className="stocks-table-wrapper">
                    <table className="stocks-table">
                      <thead><tr>
                        <th>Type</th><th>Opt</th><th>Pattern</th><th>Entry</th><th>Exit</th>
                        <th>Spot In</th><th>Buy Prem</th><th>Sell Prem</th><th>SL</th><th>Target</th>
                        <th>Bars</th><th>Vol</th><th>Exit Reason</th><th>Capital</th><th>P&L</th>
                      </tr></thead>
                      <tbody>
                        {btData.trades?.slice().reverse().map((t, i) => (
                          <tr key={i} className={t.result === "WIN" ? "trade-win" : "trade-loss"}>
                            <td><span className={`signal-badge ${t.type === "BULLISH" ? "badge-long" : "badge-short"}`}>{t.type}</span></td>
                            <td>{t.optType}</td>
                            <td><Badge text={t.oiStreak} color={CLASS_COLORS[t.oiStreak] || "var(--grey-text2)"} /></td>
                            <td style={{ fontSize: 10 }}>{t.entryDate ? new Date(t.entryDate).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                            <td style={{ fontSize: 10 }}>{t.exitDate ? new Date(t.exitDate).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                            <td>{t.entrySpot?.toFixed(0)}</td>
                            <td>₹{t.entryPremium?.toFixed(1)}</td>
                            <td>₹{t.exitPremium?.toFixed(1)}</td>
                            <td className="sl">₹{t.slPremium?.toFixed(1)}</td>
                            <td>₹{t.targetPremium?.toFixed(1)}</td>
                            <td>{t.candlesHeld}</td>
                            <td style={{ fontSize: 10, color: t.volRatio >= 1.5 ? "#34d399" : "var(--grey-text2)" }}>{t.volRatio?.toFixed(1)}x</td>
                            <td><span className={`exit-reason reason-${t.result?.toLowerCase()}`}>{t.exitReason}</span></td>
                            <td>₹{t.capital?.toLocaleString("en-IN")}</td>
                            <td className={t.netPnl >= 0 ? "positive" : "negative"}>
                              {t.netPnl >= 0 ? "+" : "-"}₹{Math.abs(t.netPnl).toLocaleString("en-IN")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {!data && !loading && activeView === "scan" && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--btn-border)" }}>
          <p>Click an index to scan OI momentum. Start auto-scan to track changes over time.</p>
          <p style={{ fontSize: 12 }}>First scan captures baseline OI. Subsequent scans show OI changes from that baseline.</p>
        </div>
      )}
    </div>
  );
}
